import { Combobox, useFilter, useToastContext } from "@ttc3k/trekker";
import {
  EnumOwnerType,
  EnumTagEntityTypes,
  EnumTagGroupKey,
  EnumTagGroupType,
  OperatorByIdDocument,
  OperatorByIdQuery,
  OperatorManyDocument,
  useOperatorUpdateMutation,
  useTagManyQuery,
} from "gql/generated";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTagGroupContext } from "shared/contextProviders/tagGroups";
import { getMultilingualString } from "shared/utils";
import { usePostHog } from "shared/hooks";
import { useFeatureFlag } from "shared/contextProviders";
import { FeatureFlags } from "shared/featureFlags";
import { useAppContext } from "useAppContext";

type TagOption = {
  value: string;
  label: string;
};

type MembershipMetadata = {
  membershipTiers?: (string | null)[] | null;
  membershipStatusTagIds?: (string | null)[] | null;
  listingAudienceTagIds?: (string | null)[] | null;
  nomenclature?: {
    membership?: string | null;
  } | null;
};

export const useBackOfficeTags = (
  operator: OperatorByIdQuery["operatorById"] | undefined,
  operatorEntityId: string | undefined,
  organizationId: string | undefined,
  onOperatorUpdated?: (updatedOperator: Partial<OperatorByIdQuery["operatorById"]>) => void,
) => {
  const { toastFactory } = useToastContext();
  const { getTagGroup } = useTagGroupContext();
  const { trackPostHogException } = usePostHog();
  const { organizations } = useAppContext();
  const memberListsEnabled = useFeatureFlag(FeatureFlags.MemberLists);
  const audiencesEnabled = useFeatureFlag(FeatureFlags.ListingAudiences);

  const tagGroupData = useMemo(
    () =>
      getTagGroup({
        key: EnumTagGroupKey.BackOffice,
        type: EnumTagGroupType.OrganizationSpecific,
      }),
    [getTagGroup],
  );

  const organization = useMemo(() => {
    return (
      organizations?.find(org => org._id === organizationId) ??
      operator?.enrollments?.find(
        enrollment => enrollment?.organization?._id === organizationId,
      )?.organization
    );
  }, [operator?.enrollments, organizationId, organizations]);
  const membershipMetadata = organization?.metadata as
    | MembershipMetadata
    | undefined;

  const membershipTierTagIdsOrdered = useMemo(
    () =>
      memberListsEnabled
        ? (membershipMetadata?.membershipTiers ?? []).filter(
            (tagId): tagId is string => !!tagId,
          )
        : [],
    [memberListsEnabled, membershipMetadata?.membershipTiers],
  );

  const membershipStatusTagIdsSet = useMemo(
    () =>
      new Set(
        memberListsEnabled
          ? (membershipMetadata?.membershipStatusTagIds ?? []).filter(
              (tagId): tagId is string => !!tagId,
            )
          : [],
      ),
    [memberListsEnabled, membershipMetadata?.membershipStatusTagIds],
  );

  const hasMembershipConfig =
    membershipTierTagIdsOrdered.length > 0 || membershipStatusTagIdsSet.size > 0;

  const {
    data: tagData,
    loading: loadingTags,
    error: errorTags,
  } = useTagManyQuery({
    variables: {
      filter: {
        tagGroup: tagGroupData?._id ? [tagGroupData._id] : null,
        owner: { entityId: organizationId, type: EnumOwnerType.Organization },
        entityTypes: [EnumTagEntityTypes.Operator],
      },
      limit: 10000,
    },
  });

  // All available back office tags for the current organization
  const backOfficeTagOptions = useMemo<TagOption[]>(
    () =>
      tagData?.tagMany
        ? _.sortBy(
            tagData.tagMany.map(tag => ({
              value: tag._id,
              label: getMultilingualString(tag.name),
            })),
            "label",
          )
        : [],
    [tagData?.tagMany],
  );

  const membershipTierTagIdsSet = useMemo(
    () => new Set(membershipTierTagIdsOrdered),
    [membershipTierTagIdsOrdered],
  );

  const membershipTierOptions = useMemo<TagOption[]>(
    () =>
      membershipTierTagIdsOrdered
        .map(id => backOfficeTagOptions.find(t => t.value === id))
        .filter((t): t is TagOption => !!t),
    [membershipTierTagIdsOrdered, backOfficeTagOptions],
  );

  const membershipStatusOptions = useMemo<TagOption[]>(
    () =>
      backOfficeTagOptions.filter(t => membershipStatusTagIdsSet.has(t.value)),
    [backOfficeTagOptions, membershipStatusTagIdsSet],
  );

  const listingAudienceTagIdsOrdered = useMemo(
    () =>
      audiencesEnabled
        ? (membershipMetadata?.listingAudienceTagIds ?? []).filter(
            (tagId): tagId is string => !!tagId,
          )
        : [],
    [audiencesEnabled, membershipMetadata?.listingAudienceTagIds],
  );

  const listingAudienceTagIdsSet = useMemo(
    () => new Set(listingAudienceTagIdsOrdered),
    [listingAudienceTagIdsOrdered],
  );

  const audienceTagOptions = useMemo<TagOption[]>(
    () =>
      listingAudienceTagIdsOrdered
        .map(id => backOfficeTagOptions.find(t => t.value === id))
        .filter((t): t is TagOption => !!t),
    [listingAudienceTagIdsOrdered, backOfficeTagOptions],
  );

  const customTagOptions = useMemo<TagOption[]>(
    () =>
      hasMembershipConfig || listingAudienceTagIdsSet.size > 0
        ? backOfficeTagOptions.filter(
            t =>
              !membershipTierTagIdsSet.has(t.value) &&
              !membershipStatusTagIdsSet.has(t.value) &&
              !listingAudienceTagIdsSet.has(t.value),
          )
        : backOfficeTagOptions,
    [
      backOfficeTagOptions,
      hasMembershipConfig,
      membershipTierTagIdsSet,
      membershipStatusTagIdsSet,
      listingAudienceTagIdsSet,
    ],
  );

  const organizationSpecific = useMemo(
    () => operator?.tags?.organizationSpecific ?? [],
    [operator?.tags?.organizationSpecific],
  );

  // We need to keep track of the selected tags for the current organization + any other organization
  const [defaultSelectedTagIds, setDefaultSelectedTagIds] = useState<string[]>(
    [],
  );
  const [selectedTagIdsForCurrentOrg, setSelectedTagIdsForCurrentOrg] =
    useState<string[]>([]);
  const [selectedTagIdsForOtherOrgs, setSelectedTagIdsForOtherOrgs] =
    useState<string[]>([]);

  useEffect(() => {
    const allOrgTagIds = backOfficeTagOptions.map(t => t.value) ?? [];
    const currentOrgTagIds = organizationSpecific
      .filter(tag => allOrgTagIds.includes(tag._id))
      .map(tag => tag._id);
    const otherOrgTagIds = organizationSpecific
      .filter(tag => !allOrgTagIds.includes(tag._id))
      .map(tag => tag._id);

    setDefaultSelectedTagIds(currentOrgTagIds);
    setSelectedTagIdsForCurrentOrg(currentOrgTagIds);
    setSelectedTagIdsForOtherOrgs(otherOrgTagIds);
  }, [backOfficeTagOptions, organizationSpecific]);

  const [filterableRegularBackOfficeTags, setFilterableRegularBackOfficeTags] =
    useState(customTagOptions);

  const { t } = useTranslation();
  const [updateOperator] = useOperatorUpdateMutation({
    refetchQueries: [OperatorByIdDocument, OperatorManyDocument],
  });

  const updateOperatorTags = async (regularTagIds: string[]) => {
    await updateOperator({
      variables: {
        entityId: operatorEntityId ?? "",
        record: {
          tags: {
            businessType: operator?.tags?.businessType?.map(t => t._id) ?? [],
            global: operator?.tags?.global?.map(t => t._id) ?? [],
            amenity: operator?.tags?.amenity?.map(t => t._id) ?? [],
            organizationSpecific: [
              ...regularTagIds,
              ...selectedTagIdsForOtherOrgs,
            ],
          },
        },
      },
    });
  };

  const buildUpdatedOrganizationSpecificTags = (
    regularTagIds: string[],
  ) => [
    ...(operator?.tags?.organizationSpecific?.filter(
      tag => !defaultSelectedTagIds.includes(tag._id),
    ) ?? []),
    ...(backOfficeTagOptions
      .filter(tag => regularTagIds.includes(tag.value))
      .map(tag => ({
        _id: tag.value,
        name: { en: tag.label },
      }))),
  ];

  const handleUpdateSelectedTagIds = async (newTagIds: string[]) => {
    try {
      setSelectedTagIdsForCurrentOrg(newTagIds);
      await updateOperatorTags(newTagIds);
      onOperatorUpdated?.({
        _entityId: operatorEntityId,
        tags: {
          businessType: operator?.tags?.businessType ?? [],
          global: operator?.tags?.global ?? [],
          amenity: operator?.tags?.amenity ?? [],
          organizationSpecific: buildUpdatedOrganizationSpecificTags(newTagIds),
        },
      });
    } catch {
      setSelectedTagIdsForCurrentOrg(defaultSelectedTagIds);
      trackPostHogException(new Error("Error updating tags"), {
        fileName: "useBackOfficeTags.ts",
        method: "handleUpdateSelectedTagIds",
        message: "Error updating tags",
      });
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    }
  };

  const { contains } = useFilter({ sensitivity: "base" });

  const { collection, filter, set } = Combobox.useCollection({
    initialItems: filterableRegularBackOfficeTags,
    filter: contains,
  });

  const handleTagInputValueSearch = (search: string) => {
    filter(search);
  };

  const handleComboboxOpenChange = (open: boolean) => {
    if (open) {
      set(customTagOptions);
    }
  };

  useEffect(() => {
    setFilterableRegularBackOfficeTags(customTagOptions);
    set(customTagOptions);
  }, [customTagOptions, set]);

  return {
    onUpdateSelectedTagIds: handleUpdateSelectedTagIds,
    onTagInputValueSearch: handleTagInputValueSearch,
    onComboboxOpenChange: handleComboboxOpenChange,
    selectedTagIdsForCurrentOrg,
    filterableBackOfficeTagsCollection: collection,
    filterableBackOfficeTags: customTagOptions,
    allBackOfficeTagOptions: backOfficeTagOptions,
    membershipTierTagIds: membershipTierTagIdsOrdered,
    membershipTierOptions,
    membershipStatusOptions,
    membershipStatusTagIds: [...membershipStatusTagIdsSet],
    hasMembershipConfig,
    memberListsEnabled,
    membershipLabel: membershipMetadata?.nomenclature?.membership ?? "Membership",
    listingAudienceTagIds: listingAudienceTagIdsOrdered,
    audienceTagOptions,
    audiencesEnabled,
    loadingTags,
    errorTags,
  };
};
